import activity from './activity/text.js';
import alarms from './alarms/text.js';
import claims from './basics/claims/texts.js';
import coneCrushers from './category/coneCrusher/texts.js';
import hammers from './category/hammer/texts.js';
import screens from './category/screen/texts.js';
import highResolution from './components/HighResolution/texts.js';
import locationHistory from './components/Map/LocationHistory/texts.js';
import insights from './insights/texts.js';
import presentation from './presentation/texts.js';

export default {
  dashboard: {
    header: {
      notes: 'Dashboard page title',
      message: 'Dashboard',
    },
    editMode: {
      header: {
        notes: 'Header text shown in action bar when editing dashboard',
        message: 'Configure dashboard',
      },
      saveSuccess: {
        notes: 'Success notification when dashboard layout is saved',
        message: 'Dashboard layout saved',
      },
    },
    actions: {
      enableEditMode: {
        notes: 'Action to start editing the dashboard',
        message: 'Configure dashboard',
      },
      renameDashboard: {
        notes: 'Action to rename dashboard',
        message: 'Rename dashboard',
      },
      removeDashboard: {
        notes: 'Action to remove dashboard',
        message: 'Remove dashboard',
      },
      removeWidget: {
        notes: 'Action to remove a widget from dashboard',
        message: 'Remove widget',
      },
      addWidget: {
        notes: 'Action to add a widget to dashboard',
        message: 'Add widget',
      },
      navigate: {
        notes: 'Action to navigate to see more details',
        message: 'View details',
      },
      shareDashboard: {
        notes: 'Action to share dashboard with other users',
        message: 'Share dashboard',
      },
      setUtilizationTarget: {
        notes: 'Action to set utilization targets',
        message: 'Set utilization targets',
      },
    },
    emptyDashboard: {
      noWidgetsToDisplay: {
        notes:
          'Empty state shown in view mode when the dashboard has no configured widgets or none are available for the selected equipment',
        message: 'There are no widgets to display in this dashboard.',
      },
    },
    emptyColumn: {
      placeholder: {
        notes: 'Placeholder text shown in empty column during edit mode',
        message: 'Drag widgets here or click add',
      },
    },
    noAvailableWidgets: {
      notes:
        'Message shown in add widget menu when all widgets have been added',
      message: 'No more widgets to add',
    },
    widgetNotAvailable: {
      notes:
        'Message shown when widget requirements are not met (e.g. missing permissions, equipment not connected)',
      message: 'Widget not available',
    },
    widgets: {
      alarms: {
        notes: 'Title of alarms widget',
        message: 'Alarms',
        openAlarms: {
          title: {
            notes: 'Title for active alarms card',
            message: 'Active alarms',
          },
        },
      },
      model3d: {
        notes: 'Title of 3D model widget',
        message: '3d model',
      },
      location: {
        notes: 'Title of location/map widget',
        message: 'Location',
      },
      keyParameters: {
        notes: 'Title of key parameters widget',
        message: 'Key parameters',
      },
      aDimension: {
        notes: 'Title of A-dimension widget',
        message: 'A-dimension',
      },
      crossSection: {
        notes: 'Title of cross section widget',
        message: 'Cross section',
        noImageAvailable: {
          notes:
            'Empty state when no cross section image available for this model',
          message: 'No cross section image available for this model',
        },
        imageAlt: {
          notes: 'Alt text for cross section image',
          message: 'Cross section',
        },
      },
      utilization: {
        notes: 'Title of utilization widget',
        message: 'Utilization',
      },
      utilizationTarget: {
        notes: 'Title of utilization target widget',
        message: 'Utilization target',
        empty: {
          notes: 'Call to action to configure load target and idle limits',
          message:
            'Utilization targets not configured. Targets enable tracking performance against daily goals',
        },
      },
      loadCycles: {
        notes: 'Title of load cycles widget',
        message: 'Load cycles',
      },
      linerWear: {
        notes: 'Title of liner wear widget',
        message: 'Liner wear',
      },
      exciterSensors: {
        notes: 'Title of exciter sensors widget',
        message: 'Exciter sensors',
      },
      bodySensors: {
        notes: 'Title of body sensors widget',
        message: 'Body sensors',
      },
      bearings: {
        notes: 'Title of bearings widget',
        message: 'Bearings',
      },
      hammerKpis: {
        notes: 'Title of hammer KPIs widget',
        message: 'KPIs',
      },
      crusherOperation: {
        notes: 'Title of crusher operation widget',
        message: 'Operation',
      },
      tombstone: {
        text: {
          notes: 'Message when widget is no longer available',
          message: 'Widget no longer available',
        },
        remove: {
          notes: 'Button to remove unavailable widget',
          message: 'Remove',
        },
        edit: {
          notes: 'Button to enter edit mode to remove unavailable widget',
          message: 'Edit',
        },
      },
    },
    errorBoundary: {
      message: {
        notes: 'Error boundary message when widget fails to load',
        message: 'Unable to load widget',
      },
    },
    members: {
      email: {
        notes: 'Column header for member email',
        message: 'Email',
      },
      access: {
        notes: 'Column header for member role',
        message: 'Role',
      },
      readOnly: {
        notes: 'Label for "read_only" access level',
        message: 'Viewer',
      },
      edit: {
        notes: 'Label for "edit" access level',
        message: 'Editor',
      },
      searchUserPlaceholder: {
        notes: 'Placeholder text for user search input',
        message: 'Search users...',
      },
      noUsersFound: {
        notes: 'Shown in user search autocomplete when no matching users found',
        message: 'No users found',
      },
    },
    share: {
      dialog: {
        title: {
          notes: 'Title of the share dashboard dialog',
          message: 'Manage members',
        },
      },
    },
    sparkline: {
      empty: {
        notes: 'Empty state when sparkline in dashboard has no data',
        message: 'No data available',
      },
      footer: {
        min: {
          notes: 'Label for minimum value in sparkline footer',
          message: 'Min',
        },
        max: {
          notes: 'Label for maximum value in sparkline footer',
          message: 'Max',
        },
        lastReading: {
          notes:
            'Label showing how long ago the last data was received, e.g. "Data from 1 hour ago"',
          message: 'Data from {{timeAgo}}',
        },
      },
      timeWindow: {
        notes: 'Time window label showing hours back, e.g. "-3h"',
        message: '-{{hours}}h',
      },
      now: {
        notes: 'Label for current time in sparkline',
        message: 'now',
      },
    },
    timeline: {
      action: {
        showAllAlarms: {
          notes: 'Title of button to show all alarms',
          message: 'View all {{count}} alarms',
        },
      },
    },
    copyDialog: {
      title: {
        notes: 'Title of the copy dashboard dialog',
        message: 'Create your copy',
      },
      helpReadOnly: {
        notes:
          'Explanation why user needs to create a copy as dashboard is not editable',
        message:
          'You have read-only access to this dashboard. To make changes, you need to create your own copy.',
      },
      nameLabel: {
        notes: 'Label for dashboard name input',
        message: 'Dashboard name',
      },
      submit: {
        notes: 'Submit button text for creating dashboard copy',
        message: 'Create copy',
      },
      success: {
        notes: 'Message when successfully creating dashboard copy',
        message: 'Dashboard copied. Drag widgets to rearrange.',
      },
    },
    removeDialog: {
      title: {
        notes: 'Title of the remove dashboard dialog',
        message: 'Remove dashboard?',
      },
      message: {
        notes: 'Confirmation message explaining dashboard removal',
        message:
          'Remove "{{title}}" from your list? The dashboard will still be accessible to other members.',
      },
      confirm: {
        notes: 'Confirm button text for removing dashboard',
        message: 'Remove',
      },
    },
    renameDialog: {
      title: {
        notes: 'Title of the rename dashboard dialog',
        message: 'Rename dashboard',
      },
      nameLabel: {
        notes: 'Label for dashboard name input field',
        message: 'Dashboard name',
      },
      submit: {
        notes: 'Submit button text for renaming dashboard',
        message: 'Rename',
      },
      success: {
        notes: 'Success notification when dashboard is renamed',
        message: 'Dashboard renamed successfully',
      },
      validation: {
        unchanged: {
          notes:
            'Validation message when new dashboard name is same as current',
          message: 'Name is unchanged',
        },
      },
    },
    notifications: {
      removed: {
        notes: 'Success notification when dashboard is removed',
        message: 'Dashboard removed',
      },
    },
    card: {
      labels: {
        subtitles: {
          max: {
            notes: 'Subtitle indicating an aggregated max value',
            message: 'Max',
          },
          percentOfTarget: {
            notes: 'Subtitle showing % of target',
            message: '% of target',
          },
          percentOfTLimit: {
            notes: 'Subtitle showing % of limit',
            message: '% of limit',
          },
        },
        scopes: {
          current: {
            notes: 'Scope label for current value',
            message: 'Current',
          },
          sinceLinerChange: {
            notes: 'Scope label for measurements since liner change',
            message: 'Since liner change',
          },
        },
      },
      crusher: {
        loadCycle: {
          title: {
            notes: 'Title for current load cycle card',
            message: 'Current load cycle',
          },
          crushing: {
            notes: 'Load cycle state when machine is crushing',
            message: 'Crushing',
          },
          idle: {
            notes: 'Load cycle state when machine is idle',
            message: 'Idle',
          },
        },
        controlMode: {
          title: {
            notes: 'Title for control mode card',
            message: 'Current mode',
          },
        },
        utilizationLoaded: {
          title: {
            notes: 'Title for loaded utilization card',
            message: 'Loaded',
          },
        },
        utilizationIdle: {
          title: {
            notes: 'Title for idle utilization card',
            message: 'Idle',
          },
        },
        utilizationTargetLoaded: {
          title: {
            notes: 'Title for loaded utilization target card',
            message: 'Loaded',
          },
          targetAchieved: {
            notes: 'Title for loaded target achieved (days met) card',
            message: 'Target achieved',
          },
        },
        utilizationTargetIdle: {
          title: {
            notes: 'Title for idle utilization target card',
            message: 'Idle',
          },
          limitBreached: {
            notes: 'Title for idle limit breached (days met) card',
            message: 'Idle limit breached',
          },
        },
        loadCyclesCount: {
          title: {
            notes: 'Title for load cycle count card',
            message: 'Load cycle count',
          },
        },
        loadCyclesDailyAvg: {
          title: {
            notes: 'Title for daily average load cycles card',
            message: 'Daily average',
          },
        },
        loadCyclesHourlyAvg: {
          title: {
            notes: 'Title for hourly average load cycles card',
            message: 'Hourly average',
          },
        },
        aDimLast: {
          title: {
            notes: 'Title for A-dim at last calibration card',
            message: 'A-dim',
          },
          subtitle: {
            notes: 'Subtitle for A-dim card (position at contact)',
            message: 'At contact position',
          },
          scope: {
            notes: 'Scope label for A-dim at last calibration',
            message: 'Last calibration',
          },
        },
        aDimCurrent: {
          title: {
            notes: 'Title for current A-dim card',
            message: 'A-dim',
          },
        },
        daysWithoutCalibration: {
          title: {
            notes: 'Title for days without calibration card',
            message: 'Days without calibration',
          },
        },
        loadedSinceChange: {
          title: {
            notes: 'Title for loaded time since liner change card',
            message: 'Loaded',
          },
        },
        loadedSinceCalibration: {
          title: {
            notes: 'Title for loaded time since calibration card',
            message: 'Loaded',
          },
          scope: {
            notes: 'Scope label - since calibration',
            message: 'Since calibration',
          },
        },
        linerInstallDate: {
          title: {
            notes: 'Title for liner install date card',
            message: 'Liner install date',
          },
        },
        power: {
          title: {
            notes: 'Title for power average card',
            message: 'Power average',
          },
        },
        pressurePeakAvg: {
          title: {
            notes: 'Title for average peak pressure card',
            message: 'Average peak pressure',
          },
        },
        css: {
          title: {
            notes: 'Title for CSS (closed side setting) card',
            message: 'CSS',
          },
        },
      },
      screen: {
        innerRing: {
          title: {
            notes: 'Title for inner ring bearing card',
            message: 'Inner ring',
          },
        },
        outerRing: {
          title: {
            notes: 'Title for outer ring bearing card',
            message: 'Outer ring',
          },
        },
        rollingElement: {
          title: {
            notes: 'Title for rolling element bearing card',
            message: 'Rolling element',
          },
        },
        strokeLength: {
          title: {
            notes: 'Title for stroke length card',
            message: 'Stroke length',
          },
        },
        strokeLengthDelta: {
          title: {
            notes: 'Title for stroke length delta card',
            message: 'Stroke length delta',
          },
        },
        strokeAngle: {
          title: {
            notes: 'Title for stroke angle card',
            message: 'Stroke angle',
          },
        },
        strokeAngleDelta: {
          title: {
            notes: 'Title for stroke angle delta card',
            message: 'Stroke angle delta',
          },
        },
        lateralDisplacement: {
          title: {
            notes: 'Title for lateral displacement card',
            message: 'Lateral displacement',
          },
        },
        exciterSpeed: {
          title: {
            notes: 'Title for exciter speed card',
            message: 'Exciter speed',
          },
        },
        thd: {
          title: {
            notes: 'Title for THD card',
            message: 'THD',
          },
        },
        rms: {
          title: {
            notes: 'Title for RMS card',
            message: 'RMS',
          },
        },
      },
      hammer: {
        workHours: {
          title: {
            notes: 'Title for work hours card',
            message: 'Work hours',
          },
        },
        impactHours: {
          title: {
            notes: 'Title for impact hours card',
            message: 'Impact hours',
          },
        },
        battery: {
          title: {
            notes: 'Title for battery card',
            message: 'Battery',
          },
        },
        timeToMaintenance: {
          title: {
            notes: 'Title for time to maintenance card',
            message: 'Time to maintenance',
          },
        },
      },
    },
    cardDef: {
      meter: {
        setpointTooltip: {
          notes: 'Prefix in set point tooltip in the meter',
          message: 'Set point: {{value}} {{unit}}',
        },
      },
      healthCard: {
        viewAlarms: {
          notes: 'Button label on health card to navigate to alarms',
          message: 'View alarms',
        },
        normal: {
          notes:
            'Health card message when all signals are OK. {{label}} is the system name (e.g. "bearings")',
          message: 'Everything OK in your {{label}} sensor data. Keep it up!',
        },
        warning: {
          notes:
            'Health card message when signals require attention. {{label}} is the system name (e.g. "Bearings")',
          message: '{{label}} require your attention.',
        },
        critical: {
          notes:
            'Health card message when signals require immediate attention. {{label}} is the system name (e.g. "Bearings")',
          message: '{{label}} require your immediate attention.',
        },
        error: {
          notes:
            'Health card message when the system health cannot be evaluated. {{label}} is the system name (e.g. "Bearings")',
          message: 'Unable to evaluate {{label}} health at the moment.',
        },
      },
    },
    noData: {
      notes: 'Fallback label when no data is available',
      message: 'No data',
    },
    hammers: {
      map: {
        empty: {
          text: {
            notes: 'Text in empty state when hammer has no position data',
            message:
              'Connect the equipment and sign terms & conditions to see the location.',
          },
        },
      },
      card: {
        totalWorkHours: {
          displayName: {
            notes: 'Title of total work hours card',
            message: 'Total work hours',
          },
        },
        totalImpactHours: {
          displayName: {
            notes: 'Title of total impact hours card',
            message: 'Total impact hours',
          },
        },
        battery: {
          displayName: {
            notes: 'Title of battery card',
            message: 'Battery',
          },
        },
        temperature: {
          displayName: {
            notes: 'Title of temperature card',
            message: 'Temperature',
          },
        },
        timeToMaintenance: {
          displayName: {
            notes: 'Title of time to maintenance card',
            message: 'Time to maintenance',
          },
        },
      },
    },
  },
  equipmentList: {
    action: {
      addEquipment: {
        notes: 'Title of add equipment button',
        message: 'Add equipment',
      },
    },
    filter: {
      actions: {
        includeSubCompanies: {
          notes: 'Include sub-companies button title',
          message: 'Include sub-companies',
        },
      },
      header: {
        notes: 'Title of equipment filter dialog',
        message: 'Filter equipment',
      },
      regionFilter: {
        notes: 'Region filter selection title',
        message: 'BLM/Region',
      },
      favourites: {
        notes: 'Specific option for Favourites in region filter',
        message: 'Favourites',
      },
      resetFilters: {
        notes: 'Reset filter button title',
        message: 'Reset filters',
      },
      textSearch: {
        notes: 'Display name of text search field',
        message: 'Text search',
      },
    },
    empty: {
      text: {
        notes: 'Text in empty state when no equipments are listed',
        message:
          'No content to show, please select a different set of filters.',
      },
      noEquipment: {
        text: {
          notes:
            'Text in empty state when no equipments exists (for selected region)',
          message: 'No equipment to show.',
        },
      },
      noMatch: {
        action: {
          notes: 'Text button for clearing equipment filters',
          message: 'Clear filters',
        },
        text: {
          notes:
            'Text in empty state when no equipments match the current filter',
          message: 'No equipment match your filters.',
        },
      },
    },
    rail: {
      title: {
        notes: 'Title when equipment list is rail',
        message: 'Equipment list',
      },
      help: {
        notes: 'Help text how to get out of rail mode',
        message: 'Close details pane in order to show the equipment list',
      },
    },
  },
  model: {
    category: {
      notes: 'Equipment category property display name',
      message: 'Category',
      values: {
        CONVEYOR: {
          notes: 'Displaytext for CONVEYOR',
          message: 'Conveyor',
        },
        CRUSHER_CONE: {
          notes: 'Displaytext for CRUSHER_CONE',
          message: 'Cone crusher',
        },
        CRUSHER_IMPACT: {
          notes: 'Displaytext for CRUSHER_IMPACT',
          message: 'Impact crusher',
        },
        CRUSHER_JAW: {
          notes: 'Displaytext for CRUSHER_JAW',
          message: 'Jaw crusher',
        },
        FEEDER: {
          notes: 'Displaytext for FEEDER',
          message: 'Feeder',
        },
        HAMMER_HYDRAULIC: {
          notes: 'Displaytext for HAMMER_HYDRAULIC ',
          message: 'Hydraulic hammer',
        },
        OTHER: {
          notes: 'Displaytext for OTHER',
          message: 'Other',
        },
        SCREEN: {
          notes: 'Displaytext for SCREEN',
          message: 'Screen',
        },
      },
    },
    connectivity: {
      notes: 'Equipment connectivity property display name',
      message: 'Connectivity',
      values: {
        unset: {
          notes: 'Connected and not connected',
          message: 'All',
        },
        connected: {
          notes: 'Connectivity connected value',
          message: 'Connected',
        },
        notConnected: {
          notes: 'Connectivity not connected value',
          message: 'Not connected',
        },
      },
      tooltip: {
        notes: 'Tooltip helptext for Connectivity',
        message:
          'A connectivity icon enables you to see the historical data but if the equipment is not connected there will be no information.',
      },
    },
    alias: {
      notes: 'Label for equipment displayName (formerly known as Name)',
      message: 'Alias',
    },
    manufacturer: {
      notes: 'Equipment manufacturer property display name',
      message: 'Manufacturer',
      validation: {
        notSandvik: {
          notes:
            'Validation message when using SANDVIK as manufacturer when creating equipment',
          message: 'Create SANDVIK equipment using other create dialogs',
        },
        uppercaseOnly: {
          notes:
            'Validation message when using non-uppercase characters as manufacturer for equipment',
          message: 'Use only uppercase characters',
        },
      },
    },
    modelNumber: {
      notes: 'Equipment model number property display name',
      message: 'Model',
    },
    modelNumberExtended: {
      notes: 'Equipment model code property display name',
      message: 'Model code',
    },
    manufacturedDate: {
      notes: 'Equipment manufactoring date property display name',
      message: 'Manufactured',
    },
    owner: {
      notes: 'Equipment owner (i.e. Company)',
      message: 'Owner',
    },
    serialNumber: {
      notes: 'Equipment serial number property display name',
      message: 'Serial number',
    },
    region: {
      notes: 'Region property display name',
      message: 'BLM/Region',
    },
    site: {
      notes: 'Site property display name',
      message: 'Site',
    },
    alarmCount: {
      notes: 'Alarm count display name',
      message: 'Active alerts',
    },
    company: {
      notes: 'Company property display name',
      message: 'Company',
    },
    parentRegion: {
      notes: 'Label for parent region field',
      message: 'Parent region',
    },
    prototype: {
      notes: 'Label for the is prototype toggle',
      message: 'Prototype',
    },
    rd3SerialNumber: {
      notes: 'RD3 serial number property display name',
      message: 'RD3 serial number',
    },
    serviceDue: {
      notes: 'Time to next service display name',
      message: 'Service due in (h)',
    },
    lastConnected: {
      notes: 'Last connected display name',
      message: 'Last connected',
    },
    lastService: {
      notes: 'Time since previous service display name',
      message: 'Last service (h)',
    },
    workHours: {
      notes: 'Total amount of work hours',
      message: 'Work hours (h)',
    },
    rentedTo: {
      notes:
        'When rented, label for company name of company that is renting the current equipment',
      message: 'Rented to',
    },
    rentalUse: {
      notes: 'Rental use, label for field with values Yes/No',
      message: 'Rental use',
    },
    favourite: {
      notes: 'Label for favourite',
      message: 'Favourite',
    },
    automationSystem: {
      unknown: {
        notes: 'Label when unable to determine automation system',
        message: 'Unknown automation system',
      },
    },
    validation: {
      rd3SerialNumberInvalid: {
        notes:
          'The provided RD3 serial number is invalid (malformed, already in use, etc)',
        message: 'Please provide valid RD3 serial number',
      },
    },
  },
  basics: {
    header: {
      notes: 'Equipment basics page title',
      message: 'Equipment basics',
    },
    sections: {
      general: {
        header: {
          notes: 'General section header',
          message: 'General information',
        },
      },
      dealerSettings: {
        header: {
          notes: 'Dealer settings section header',
          message: 'Dealer settings',
        },
        properties: {
          sandvikEquipmentRange: {
            notes: 'Equipment range display name',
            message: 'Sandvik equipment range',
          },
          otherEquipmentRange: {
            notes: 'Other brand equipment range display name',
            message: 'Other brand equipment range',
          },
        },
      },
      company: {
        header: {
          notes: 'Company section header',
          message: 'Company',
        },
        properties: {
          assignedTo: {
            notes: 'Assigned to display name',
            message: 'Assigned to (attachments)',
          },
          companyCode: {
            notes: 'Company code display name',
            message: 'Company code (dealers)',
          },
          shippingDate: {
            notes: 'Shipping date display name',
            message: 'Shipping date',
          },
        },
      },
      location: {
        header: {
          notes: 'Location section header',
          message: 'Location',
        },
        properties: {
          name: {
            notes: 'Name display name',
            message: 'Name',
          },
          siteId: {
            notes: 'Site id display name',
            message: 'Site ID',
          },
        },
      },
      equipmentConfiguration: {
        header: {
          notes: 'Equipment configuration section header',
          message: 'Equipment configuration',
        },
      },
      equipmentAuxilliaries: {
        header: {
          notes: 'Equipment auxilliaries section header',
          message: 'Equipment auxilliaries',
        },
      },
      manuals: {
        header: {
          notes: 'Manuals section header',
          message: 'Manuals information',
        },
        model: {
          noOfPages: {
            notes: 'Number of pages for the manual',
            message: '{{no}} pages',
          },
        },
      },
      serviceAgreement: {
        header: {
          notes: 'Service agreement section header',
          message: 'Service agreement',
        },
      },
      resources: {
        header: {
          notes: 'Resources section header',
          message: 'Resources information',
        },
      },
    },
  },
  data: {
    charts: {
      bucketWidth: {
        notes:
          'Footer text in charts showing bucket width and operation information',
        message:
          'Data point aggregation: {{bucketWidth}} ({{bucketOperation}})',
      },
    },
    locationHistory,
    header: {
      notes: 'Equipment data page title',
      message: 'Equipment data',
    },
  },
  discover: {
    header: {
      notes: 'Discover data page title',
      message: 'Discover data',
    },
    menuItem: {
      notes: 'Menu item for discover data',
      message: 'Data views',
    },
    actions: {
      createDataView: {
        notes: 'Action to create a data view',
        message: 'New data view',
      },
      deleteDataView: {
        notes: 'Action to delete a data view',
        message: 'Delete view',
      },
      addDataView: {
        notes: 'Action to add existing data view to the page',
        message: 'Add data view',
      },
      configurePlot: {
        notes: 'Action to configure plots for data view',
        message: 'Select signal',
      },
    },
    dataView: {
      header: {
        notes: 'Header in pane showing data views',
        message: 'View',
      },
      morePlots: {
        notes: 'Number of additional plots configured for data view',
        message: '+{{count}} more',
      },
      create: {
        header: {
          notes: 'Header when creating a data view',
          message: 'Create',
        },
        instruction: {
          notes: 'Instruction how to create a data view',
          message: 'Please select a signal to populate view',
        },
      },
      defaultNewViewName: {
        notes: 'Default name when creating new data view',
        message: 'New analysis',
      },
    },
    dataResolution: {
      aggregated: {
        notes: 'Aggregated data resolution',
        message: 'Aggregated',
      },
      highResolution: {
        notes: 'High resolution data resolution',
        message: 'High resolution',
      },
      other: {
        notes: 'Other data resolution',
        message: 'Other',
      },
    },
  },
  insights: {
    header: {
      notes: 'Insights page title',
      message: 'Insights',
    },
    ...insights,
  },
  noticeBoard: {
    actions: {
      notes: 'Title of create note button',
      message: 'Create note',
    },
    header: {
      notes: 'Notice board header',
      message: 'Notice board',
    },
    messageDetails: {
      imageSectionHeader: {
        notes: 'Header for images section of the message',
        message: 'Images',
      },
    },
    list: {
      type: {
        notes: 'Type header name',
        message: 'Type',
      },
      content: {
        notes: 'Content header name',
        message: 'Content',
      },
      topic: {
        notes: 'Topic header name',
        message: 'Topic',
      },
      status: {
        notes: 'Status header name',
        message: 'Status',
      },
      resolvedBy: {
        notes: 'Resolved by header name',
        message: 'Resolved by',
      },
      creator: {
        notes: 'Creator header name',
        message: 'Creator',
      },
    },
    model: {
      attachments: {
        notes: 'Report attachments property display name',
        message: 'Attachments',
      },
      body: { notes: 'Report body property display name', message: 'Body' },
      createdAt: {
        notes: 'Report created date property display name',
        message: 'Created',
      },
      createdBy: {
        notes: 'Report created by property display name',
        message: 'Created by',
      },
      id: { notes: 'Report id property display name', message: 'Id' },
      subject: {
        notes: 'Report subject property display name',
        message: 'Subject',
      },
    },
    help: {
      notes: 'Little i helptext for Notice board list view',
      message: 'The capability of adding a note is available on tablets',
    },
  },
  partsCatalog: {
    header: {
      notes: 'Parts (a.k.a Catalog) page title',
      message: 'Parts',
    },
  },
  multiSelection: {
    notAvailable: {
      text: {
        notes:
          'Text shown to explain that multi selection is not available for this view',
        message: 'This content is not available for multiple equipment.',
      },
      clearSelectionButton: {
        notes: 'Title for clear selection button',
        message: 'Clear selection',
      },
    },
    limitExceeded: {
      text: {
        notes:
          'Text shown to inform the user that their selection has exceeded the limit',
        message:
          'This content is not available. To show data, please select {{value}} equipments or less.',
      },
    },
    moreChip: {
      notes:
        'Text in chip showing that more equipment is selected than can be displayed as chips.',
      message: '+{{count}} more',
    },
    mixedTimezones: {
      notes:
        'Shown when selected equipment are in different timezones and the page requires a single timezone',
      message: 'Select equipment in the same time zone',
    },
  },
  selection: {
    actions: {
      removeFromSelection: {
        notes: 'Action that removes equipment from the current selection',
        message: 'Remove from selection set',
      },
    },
  },
  sample: {
    bucketOperation: {
      average: {
        notes: 'Short name for bucket operation average',
        message: 'AVG',
      },
      sum: {
        notes: 'Short name for bucket operation sum',
        message: 'SUM',
      },
      min: {
        notes: 'Short name for bucket operation minimum',
        message: 'MIN',
      },
      max: {
        notes: 'Short name for bucket operation maximum',
        message: 'MAX',
      },
      min_max_average: {
        notes: 'Short name for bucket operation min-max-average',
        message: 'MIN_MAX_AVG',
      },
    },
  },
  permits: {
    noAccess: {
      notes:
        'Instruction text when functionality is not available for equipment',
      message:
        'Feature is not enabled for selected equipment. Contact your SANDVIK Sales Representative to enable it.',
    },
    equipmentUnavailable: {
      notes: 'Empty state text when fetching equipment failed',
      message:
        'The equipment does not exist, or you do not have access to view it.',
    },
  },
  activity,
  alarms,
  claims,
  presentation,
  sensor: {
    side: {
      right: {
        notes: 'Name for right side placement of sensors',
        message: 'Right',
      },
      left: {
        notes: 'Name for left side placement of sensors',
        message: 'Left',
      },
    },
    vertical: {
      top: {
        notes: 'Label for top vertical placement of sensors',
        message: 'Top',
      },
      bottom: {
        notes: 'Label for bottom vertical placement of sensors',
        message: 'Bottom',
      },
    },
    position: {
      beam: {
        notes: 'Name for sensor position "beam"',
        message: 'Beam',
      },
      feed: {
        notes: 'Name for sensor position "feed"',
        message: 'Feed',
      },
      feed_top: {
        notes: 'Name for sensor position "feed top"',
        message: 'Feed top',
      },
      feed_bottom: {
        notes: 'Name for sensor position "feed bottom"',
        message: 'Feed bottom',
      },
      center: {
        notes: 'Name for sensor position "center"',
        message: 'Center',
      },
      center_top: {
        notes: 'Name for sensor position "center top"',
        message: 'Center top',
      },
      center_bottom: {
        notes: 'Name for sensor position "center bottom"',
        message: 'Center bottom',
      },
      exciter: {
        notes: 'Name for sensor position "exciter"',
        message: 'Exciter',
      },
      beam_feed_exciter: {
        notes: 'Name for sensor position "beam feed exciter"',
        message: 'Beam feed exciter',
      },
      beam_discharge_exciter: {
        notes: 'Name for sensor position "beam discharge exciter"',
        message: 'Beam discharge exciter',
      },
      discharge: {
        notes: 'Name for sensor position "discharge"',
        message: 'Discharge',
      },
      discharge_top: {
        notes: 'Name for sensor position "discharge top"',
        message: 'Discharge top',
      },
      discharge_bottom: {
        notes: 'Name for sensor position "discharge bottom"',
        message: 'Discharge bottom',
      },
    },
    connected: {
      notes: 'Label showing number of connected and total sensors',
      message: '{{connected}}/{{total}} active',
    },
  },
  essentials: {
    staleData: {
      notes: 'Text when data is stale for equipment',
      message: `{{date}}\nThe latest data was received more than {{threshold}}.`,
    },
    isRunning: {
      running: {
        notes: 'Text when equipment is running',
        message: 'Running',
      },
      stopped: {
        notes: 'Text when equipment is stopped',
        message: 'Stopped',
      },
      unknown: {
        notes: "Text when it's unknown if the equipment is running",
        message: 'Running: -',
      },
    },
    fieldbus: {
      connected: {
        notes: 'Text when fieldbus is connected',
        message: 'Fieldbus connected',
      },
      notConnected: {
        notes: 'Text when fieldbus is not connected',
        message: 'Fieldbus not connected',
      },
      unknown: {
        notes: 'Text when fields connectivity is unknown',
        message: 'Fieldbus: -',
      },
    },
    timeZone: {
      label: {
        notes:
          'Chip label showing UTC offset of equipment timezone, e.g. "UTC+2"',
        message: 'UTC{{offset}}',
      },
      tooltipSame: {
        notes:
          'Tooltip on timezone chip when equipment is in same timezone as user',
        message: 'Data is shown in equipment local time (UTC{{offset}})',
      },
      tooltipDifferent: {
        notes:
          'Tooltip on timezone chip when equipment timezone differs from user timezone',
        message:
          'Equipment is in a different timezone (UTC{{equipmentOffset}}). Your local time is UTC{{userOffset}}. Data is shown in equipment local time.',
      },
    },
  },
  coneCrushers,
  hammers,
  screens,
  annotation: {
    create: {
      header: {
        notes: 'Header in create annotation dialog',
        message: 'Add annotation',
      },
      filterAllClassifications: {
        notes:
          'Label for action to remove filter, i.e. show all classifications',
        message: 'All',
      },
      successToast: {
        notes: 'Message in toast when annotations is successfully created',
        message: 'Annotation created successfully',
      },
    },
    model: {
      sentiment: {
        notes: 'Annotation sentiment property display name',
        message: 'Sentiment',
        values: {
          good: {
            notes: 'Display name for good sentiment on annotations',
            message: 'Good',
          },
          neutral: {
            notes: 'Display name for neutral sentiment on annotations',
            message: 'Neutral',
          },
          bad: {
            notes: 'Display name for bad sentiment on annotations',
            message: 'Bad',
          },
        },
      },
      classification: {
        notes: 'Annotation classification property display name',
        message: 'Name',
      },
      description: {
        notes: 'Annotation description property display name',
        message: 'Description',
      },
      signal: {
        notes: 'Annotation related signals property display name',
        message: 'Signals',
      },
      timestamp: {
        notes: 'Annotation timestamp property display name',
        message: 'At',
        interval: {
          notes: 'Annotation time property when an interval is selected',
          message: 'Between',
        },
        from: {
          notes: 'Annotation from timestamp property display name',
          message: 'From',
        },
        to: {
          notes: 'Annotation to timestamp property display name',
          message: 'To',
          validation: {
            afterFrom: {
              notes:
                "Validation message when end time for annotation occurs before it's start time",
              message:
                'The selected to date cannot be earlier than the from date.',
            },
          },
        },
      },
    },
    timeSelection: {
      timestamp: {
        notes: 'Label when selecting a single timestamp for an annotation',
        message: 'Timestamp',
      },
      interval: {
        notes: 'Label when selecting a time interval for an annotation',
        message: 'Time range',
      },
    },
    actions: {
      addActionDialog: {
        notes: 'Label for action to launch add annotation dialog',
        message: 'Add annotation',
      },
    },
  },
  components: {
    highResolution,
    carousel: {
      controlButton: {
        playTooltip: {
          notes:
            'Tooltip for action to start cycling equipment automatically in carousel',
          message: 'Start autoplay',
        },
        pauseTooltip: {
          notes:
            'Tooltip for action to pause cycling equipment automatically in carousel',
          message: 'Pause autoplay',
        },
      },
    },
  },
  termsAndConditions: {
    allExcluded: {
      notes:
        'Empty state shown when selected equipment includes an owner that has not accepted the terms and conditions',
      message:
        "Terms & Conditions for this equipment's owner have not been accepted.",
    },
  },
};
